// Opening a device's console in a window of its own (#537).
//
// WHY A WINDOW AND NOT A PANEL. An operator troubleshooting a venue works
// several devices at once, and a panel on the device page dies when they
// navigate to the next device. `Devices.tsx` used to carry the opposite rule --
// "one console at a time on the page ... several open at once is several
// long-lived connections held for a page nobody is looking at" -- and the
// reason is the half that changed: a window the operator opened deliberately
// and put on a second monitor IS a page somebody is looking at. What bounds
// concurrency is not the page any more; it is the cell's own ceiling
// (`CONSOLE_SESSIONS_PER_CELL`, a §29 placeholder this item does not touch),
// which refuses in the failing window and says so.
//
// WHAT THIS DOES NOT CONSTRUCT. The address below is an SPA route built from a
// serial. It is NOT the console's attach address: §11.2 says "Clients use the
// `attach_path` the open returns and never construct it", and that rule is kept
// where it has always been -- `Console.tsx` opens the session with
// `POST /devices/{serial}/console` inside the new document and dials exactly the
// `attach_path` that answer carried. Nothing is handed between the two windows,
// which is also why the opener does not open the session and pass it along.

/** The SPA route a console window is opened at.
 *
 * Not an API path. `tools/uiclientcheck` reads every versioned path literal
 * under `web/` -- including the ones inside comments, which is why this
 * sentence does not spell one -- and this route carries no version prefix
 * because it is the application's own address, not the document's. */
export function consoleWindowPath(serial: string): string {
  return `/console/${encodeURIComponent(serial)}`;
}

/** The browsing-context name, one per device.
 *
 * Re-opening a device whose console is already up focuses that window instead of
 * making a second one -- which is the browser's own behaviour for a named
 * target, and is also what keeps one device from spending two of the cell's
 * session slots. Note this is NOT what an operating system's window switcher
 * shows: that is `document.title`, which `ConsoleWindow` sets to the device's
 * name. */
export function consoleWindowName(serial: string): string {
  return `zenith-console-${serial}`;
}

/** What a console window owes the operator when it opens: this much of the
 * device's own output, at `CONSOLE_FONT_DEFAULT`, before anything is pressed
 * (#582). 80x24 is the geometry every CLI on these devices is written for --
 * a switch's `show` tables and an access point's shell both assume it -- and
 * it is what the window's size below is computed from, so the type size and
 * the window can no longer be chosen without reference to each other, which
 * is how #537 came to open a 620px-tall window that held 18 rows. */
export const CONSOLE_TARGET_COLS = 80;
export const CONSOLE_TARGET_ROWS = 24;

/** One terminal cell at `CONSOLE_FONT_DEFAULT`, in CSS pixels. MEASURED, NOT
 * DERIVED: xterm sizes its cell from the font the browser resolves for
 * `courier-new, courier, monospace`, and that is the browser's and the
 * operating system's to decide. Chrome 153 on macOS gives 9x17 at 15px
 * (2026-09-20, `docs/evidence/2026-09-20-a-console-window-opens-at-80x24-once-its-terminal-is-fitted-and-the-window-is-sized-to-the-type.md`).
 * A platform whose monospace face is wider gets fewer columns than the target
 * from the same window, and the A- control is still there for it. */
export const CONSOLE_CELL_WIDTH = 9;
export const CONSOLE_CELL_HEIGHT = 17;

/** What the window spends around the terminal, in CSS pixels, measured on the
 * same run. These are the real cost of `.console-window` and of the panel the
 * terminal sits in, and they are the reason the window is not simply
 * "80 columns wide": most of a console window is not console.
 *
 * Width: the window's padding (16+16), the panel's border (1+1) and padding
 * (16+16), and the 15px column xterm keeps for its scrollbar -- 81.
 *
 * Height, top to bottom: padding 16; the head at its TALLEST, 102, which is
 * the three-line password sentence a device of unrecognised source gets
 * (the access point's and the switch's are two lines, 81); gap 12; the panel's
 * border, padding and head (1+16+35), the 24 between the head and the
 * terminal, the 24 between the terminal and the sentence under it, that
 * sentence (42) and its margin (13), padding and border again (16+1); the
 * panel's own bottom margin (16) and the window's padding (16) -- 333. Plus
 * one: Chrome gives a popup a content box one pixel SHORTER than the `height`
 * it was asked for (`height=620` opened at `innerHeight 619`, measured). */
export const CONSOLE_CHROME_WIDTH = 81;
export const CONSOLE_CHROME_HEIGHT = 334;

/** The narrowest window whose head keeps the type control and the password
 * column beside the title rather than wrapped under it. Measured at 900: the
 * head is one row of tools, 81 or 102px tall, and the height budget above is
 * counted against that. At the terminal's own 801 the tools wrap and the head
 * takes two rows, so the terminal's need is not the binding one -- the head's
 * is. */
export const CONSOLE_HEAD_WIDTH = 900;

/** The console window's size, in CSS pixels: the target geometry, plus what
 * the window spends around it, and no narrower than the head needs. 900x742
 * on the measured platform, where #537's 900x620 gave 91x18 once the
 * terminal was actually fitted (#624) and 80x24 clipped by six rows before. */
export const CONSOLE_WINDOW_WIDTH = Math.max(CONSOLE_HEAD_WIDTH, CONSOLE_TARGET_COLS * CONSOLE_CELL_WIDTH + CONSOLE_CHROME_WIDTH);
export const CONSOLE_WINDOW_HEIGHT = CONSOLE_TARGET_ROWS * CONSOLE_CELL_HEIGHT + CONSOLE_CHROME_HEIGHT;

/** What the browser puts above the content box of a popup: its title bar and
 * address strip. Chrome 153 on macOS: `outerHeight 686` for `innerHeight 619`.
 * The cascade below is held on the screen by the window's OUTER edge, because
 * a browser that is asked to put a window's frame past the bottom of the
 * screen moves it up, onto the place before it -- which is #550's defect by
 * a route the content-box arithmetic cannot see. */
export const CONSOLE_WINDOW_FRAME = 67;

/** The screen the cascade is sized against: a 1440x900 laptop with the menu
 * bar taken out of the height, which is the smallest an operator is expected
 * to work several consoles from. Stated here because #582 made the window
 * 122px taller and the cascade's room on that screen went from 255px to 66,
 * and a step chosen without a screen to hold it against is the arithmetic
 * that put the last places on top of one another. */
export const CONSOLE_CASCADE_SCREEN = { width: 1440, height: 875 };

/** How far each console window is placed from the one before it, and how many
 * places there are before the cascade returns to the start.
 *
 * WHY THIS EXISTS AT ALL (#550). #537 opened every console with
 * `width` and `height` and no `left` or `top`, which is not "wherever the
 * browser likes": Chrome puts every one of them at the same screen origin.
 * Measured on Chrome 153 / macOS, two devices opened in turn gave two live,
 * correctly named, still-attached windows at `screenX 0, screenY 31` --
 * pixel-identical, so the second covered the first exactly. It was reported as
 * the second console reloading the first window, and it is not: both sessions
 * are up and the operator can see one of them.
 *
 * WHY A CASCADE AND NOT A LAYOUT. #550 puts tiling explicitly out of scope --
 * "the operator arranges the windows". So this owes exactly one thing: that a
 * new console window is visibly its own window rather than hidden behind the
 * last one. A diagonal step is the smallest answer that pays it.
 *
 * WHY THE STEP IS NOT SQUARE ANY MORE (#582). The window grew to hold 24
 * rows, and on the stated screen the eight places no longer fit 36px apart
 * vertically: the room is `875 - 67 - 742 = 66px`, and seven steps of 36 is
 * 252. Rather than fewer places (which puts a ninth console back on the
 * first) the vertical step is what the room allows, 9px, while the horizontal
 * one stays 36 -- there is 540px of width to spare. A window 36px to the
 * right of the last one is visibly its own window, which is all #550 asked. */
export const CONSOLE_CASCADE_STEP = 36;
export const CONSOLE_CASCADE_SLOTS = 8;
export const CONSOLE_CASCADE_RISE = Math.min(
  CONSOLE_CASCADE_STEP,
  Math.floor((CONSOLE_CASCADE_SCREEN.height - CONSOLE_WINDOW_FRAME - CONSOLE_WINDOW_HEIGHT) / (CONSOLE_CASCADE_SLOTS - 1)),
);

/** Where the window in a given cascade place goes, held on the screen.
 *
 * Pure, and the reason the placement is testable at all: `window.open`'s own
 * positioning cannot be observed from a test environment that has no screen.
 * `avail*` is what the browser says is usable, and it is read defensively --
 * jsdom reports `0`, which would otherwise clamp every window to the origin
 * and reintroduce the defect in the one place that checks for it. */
export function consoleWindowOffset(slot: number, avail?: { width: number; height: number }): { left: number; top: number } {
  const i = ((slot % CONSOLE_CASCADE_SLOTS) + CONSOLE_CASCADE_SLOTS) % CONSOLE_CASCADE_SLOTS;
  const left = i * CONSOLE_CASCADE_STEP;
  const top = i * CONSOLE_CASCADE_RISE;
  if (!avail || avail.width <= 0 || avail.height <= 0) return { left, top };
  return {
    left: Math.max(0, Math.min(left, avail.width - CONSOLE_WINDOW_WIDTH)),
    top: Math.max(0, Math.min(top, avail.height - CONSOLE_WINDOW_HEIGHT)),
  };
}

const SLOT_KEY = "zenith.console.cascade";

/** The cascade place this device's console window gets, stable for the device.
 *
 * Stable rather than counted, because a place has to survive the operator
 * reloading the device list or working from a second tab -- a counter that
 * restarts puts the next console back on top of an existing one, which is the
 * defect again by a slower route. The same serial therefore always computes the
 * same place, which also means re-opening a device is a no-op for position:
 * `window.open` ignores the feature string when it reuses a named window, and
 * that reuse is what #550's Scope keeps.
 *
 * The table is a per-viewer convenience like the type size, guarded the same
 * way, and reset rather than grown without bound -- what it protects is the
 * next few windows on screen, not a history. */
export function consoleWindowSlot(serial: string): number {
  let table: { n: number; s: Record<string, number> } = { n: 0, s: {} };
  try {
    const raw = window.localStorage.getItem(SLOT_KEY);
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        const c = parsed as { n?: unknown; s?: unknown };
        if (typeof c.n === "number" && Number.isFinite(c.n) && c.s && typeof c.s === "object") {
          table = { n: c.n, s: c.s as Record<string, number> };
        }
      }
    }
  } catch {
    // No storage, or storage that refuses, or a value this control did not
    // write: a fresh table still places the window somewhere, which is the
    // whole job.
  }
  const held = table.s[serial];
  if (typeof held === "number" && Number.isFinite(held)) return held;
  const slot = table.n % CONSOLE_CASCADE_SLOTS;
  const next = { n: table.n + 1, s: Object.keys(table.s).length >= CONSOLE_CASCADE_SLOTS * 4 ? {} : table.s };
  next.s[serial] = slot;
  try {
    window.localStorage.setItem(SLOT_KEY, JSON.stringify(next));
  } catch {
    // The window still opens in this place; only remembering it is lost.
  }
  return slot;
}

/** Opens (or focuses) the console window for a device.
 *
 * Returns the window when the browser gave one, and `null` when it refused --
 * a popup blocker, or a headless environment. The caller says so rather than
 * appearing to have done nothing. */
export function openConsoleWindow(serial: string): Window | null {
  // The height handed to the clamp is what is left for the CONTENT box once
  // the browser's frame is on the screen, so the outer edge is what is held.
  const { left, top } = consoleWindowOffset(
    consoleWindowSlot(serial),
    typeof window.screen === "undefined" ? undefined : { width: window.screen.availWidth, height: window.screen.availHeight - CONSOLE_WINDOW_FRAME },
  );
  const features = `popup=yes,width=${CONSOLE_WINDOW_WIDTH},height=${CONSOLE_WINDOW_HEIGHT},left=${left},top=${top}`;
  const w = window.open(consoleWindowPath(serial), consoleWindowName(serial), features);
  w?.focus();
  return w ?? null;
}

/** The terminal's type size, in CSS pixels.
 *
 * WHY 15 AND NOT 13. The console was `fontSize: 13` from the day it was written,
 * which is the editor default rather than a decision -- and a console is read at
 * arm's length on whatever monitor the operator has, not at a laptop's reading
 * distance. 15 is the default here and the control below is what settles the
 * rest, because "readable without leaning in" is a property of the person and
 * the screen and no single number is true of both.
 *
 * WHAT MAKES 15 FIT (#582). The window is sized to the type, not the type to
 * the window: `CONSOLE_WINDOW_WIDTH` and `CONSOLE_WINDOW_HEIGHT` above are
 * computed from 80 columns and 24 rows at THIS size, from the cell that size
 * measures on the reference platform, and from what the window spends around
 * the terminal. Lowering this to make the output fit -- which is what an
 * operator did with the A- control when the window was 620px tall, down to a
 * 12 this comment already rejects -- is the wrong side of that relation. */
export const CONSOLE_FONT_DEFAULT = 15;
export const CONSOLE_FONT_MIN = 11;
export const CONSOLE_FONT_MAX = 28;

const FONT_KEY = "zenith.console.font-size";

/** The size this viewer last chose, or the default.
 *
 * A per-viewer convenience and nothing more: it is this browser's, it never
 * reaches the server, and every read and write is guarded because
 * `localStorage` throws in a private window and comes back empty after site
 * data is cleared. A stored value outside the bounds is ignored rather than
 * clamped -- it did not come from this control. */
export function readConsoleFontSize(): number {
  try {
    const raw = window.localStorage.getItem(FONT_KEY);
    const n = raw === null ? NaN : Number(raw);
    if (Number.isFinite(n) && n >= CONSOLE_FONT_MIN && n <= CONSOLE_FONT_MAX) return n;
  } catch {
    // No storage, or storage that refuses: the default is a correct answer.
  }
  return CONSOLE_FONT_DEFAULT;
}

export function writeConsoleFontSize(size: number): void {
  try {
    window.localStorage.setItem(FONT_KEY, String(size));
  } catch {
    // The size still applies to this window; only remembering it is lost.
  }
}

/** The next size in a direction, held inside the bounds. */
export function stepConsoleFontSize(size: number, by: number): number {
  return Math.min(CONSOLE_FONT_MAX, Math.max(CONSOLE_FONT_MIN, size + by));
}
